import React, { useEffect, useState } from "react";
import { Loader, Save, Archive, Trash2, RefreshCw, Radio as RadioIcon, AlertTriangle, FileText, Bell } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

/**
 * StreamReviewTab — admin queue for live broadcasts that ended without an
 * explicit host finalize action. Lives at /admin?tab=stream_review.
 *
 * For each pending row, the admin can:
 *  - "Save as replay"  → create a /api/videos VOD entry, dequeue
 *  - "Keep as draft"   → mark draft (hidden from feeds), dequeue
 *  - "Delete"          → hard delete + chat/guest purge (audit row written)
 */
export default function StreamReviewTab() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState({}); // {stream_id: "save"|"draft"|"delete"}
  const [confirmDel, setConfirmDel] = useState(null); // stream object pending confirm
  const [notifyTarget, setNotifyTarget] = useState(null); // stream object for the notify modal
  const [notifyMsg, setNotifyMsg] = useState("");

  const refresh = async () => {
    try {
      const r = await api.get("/admin/streams/pending-review");
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load review queue");
      setItems([]);
    }
  };

  useEffect(() => { refresh(); }, []);

  const act = async (id, action, body) => {
    setBusy((b) => ({ ...b, [id]: action }));
    try {
      if (action === "save") {
        await api.post(`/admin/streams/${id}/save-as-replay`, body || {});
        toast.success("Saved as replay video");
      } else if (action === "draft") {
        await api.post(`/admin/streams/${id}/keep-as-draft`);
        toast.success("Kept as draft — hidden from feeds");
      } else if (action === "delete") {
        await api.delete(`/admin/streams/${id}`);
        toast.success("Stream permanently deleted");
        setConfirmDel(null);
      } else if (action === "notify") {
        await api.post(`/admin/streams/${id}/notify-host`, body || {});
        toast.success("Host notified");
        setNotifyTarget(null);
        setNotifyMsg("");
        // No optimistic drop — notify doesn't change queue state.
        return;
      }
      // Optimistically drop the row
      setItems((rows) => (rows || []).filter((r) => r.id !== id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setBusy((b) => { const c = { ...b }; delete c[id]; return c; });
    }
  };

  if (items === null) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader className="w-7 h-7 animate-spin text-tv-accent" />
      </div>
    );
  }

  return (
    <div data-testid="stream-review-tab" className="space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-tv-accent font-bold inline-flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Pending admin review
          </div>
          <h2 className="font-display font-black text-2xl sm:text-3xl text-white mt-1">Stream review queue</h2>
          <p className="text-white/55 text-sm mt-1 max-w-xl">
            Streams that ended without the host running a save / draft / delete action.
            Review each one, then keep it as a replay, save it as a draft, or delete permanently.
          </p>
        </div>
        <button
          data-testid="stream-review-refresh"
          onClick={refresh}
          className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-bold text-sm px-4 py-2 rounded-full"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </header>

      {items.length === 0 ? (
        <div data-testid="stream-review-empty" className="rounded-2xl border border-white/10 bg-white/5 p-10 text-center">
          <RadioIcon className="w-10 h-10 text-emerald-400/70 mx-auto mb-3" />
          <h3 className="font-display font-black text-xl text-white">All clear — nothing to review</h3>
          <p className="text-white/55 text-sm mt-1">When a host ends a stream without choosing what to do with it, the row will land here.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((s) => (
            <div
              key={s.id}
              data-testid={`stream-review-row-${s.id}`}
              className="rounded-2xl border border-white/10 bg-white/5 p-4 sm:p-5 flex flex-wrap gap-4 items-start"
            >
              <div className="w-32 aspect-video bg-black rounded-lg overflow-hidden shrink-0">
                {s.thumbnail_url
                  ? <img src={s.thumbnail_url} alt="" className="w-full h-full object-cover" />
                  : <div className="w-full h-full bg-gradient-to-br from-fuchsia-900 via-rose-900 to-black flex items-center justify-center">
                      <RadioIcon className="w-6 h-6 text-white/40" />
                    </div>
                }
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-display font-black text-lg text-white">{s.title || "Untitled"}</h3>
                  <span className="inline-flex items-center gap-1 bg-amber-500/20 text-amber-200 border border-amber-500/30 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded">
                    {s.ended_reason || s.ended_via || "unsaved"}
                  </span>
                </div>
                <div className="text-white/55 text-xs mt-1">
                  Host: <strong className="text-white/75">{s.creator_name || s.creator_id}</strong> ·
                  Ended: {s.ended_at ? new Date(s.ended_at).toLocaleString() : "—"} ·
                  Category: {s.category || "—"}
                </div>
                {s.description && (
                  <p className="text-white/70 text-sm mt-2 line-clamp-2">{s.description}</p>
                )}
                <div className="text-white/35 text-[10px] mt-2 font-mono break-all">id: {s.id}</div>
              </div>
              <div className="flex flex-wrap gap-2 self-center">
                <button
                  data-testid={`stream-review-save-${s.id}`}
                  disabled={busy[s.id] === "save"}
                  onClick={() => act(s.id, "save")}
                  className="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 text-black font-display font-black px-3.5 py-2 rounded-full text-xs disabled:opacity-60"
                >
                  {busy[s.id] === "save" ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                  Save as replay
                </button>
                <button
                  data-testid={`stream-review-draft-${s.id}`}
                  disabled={busy[s.id] === "draft"}
                  onClick={() => act(s.id, "draft")}
                  className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white font-bold px-3.5 py-2 rounded-full text-xs disabled:opacity-60"
                >
                  {busy[s.id] === "draft" ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Archive className="w-3.5 h-3.5" />}
                  Keep as draft
                </button>
                <button
                  data-testid={`stream-review-notify-${s.id}`}
                  disabled={busy[s.id] === "notify"}
                  onClick={() => { setNotifyTarget(s); setNotifyMsg(""); }}
                  className="inline-flex items-center gap-1.5 bg-sky-500/15 hover:bg-sky-500/30 text-sky-200 hover:text-white font-bold px-3.5 py-2 rounded-full text-xs border border-sky-500/30 disabled:opacity-60"
                >
                  <Bell className="w-3.5 h-3.5" /> Notify host
                </button>
                <button
                  data-testid={`stream-review-delete-${s.id}`}
                  disabled={busy[s.id] === "delete"}
                  onClick={() => setConfirmDel(s)}
                  className="inline-flex items-center gap-1.5 bg-rose-500/20 hover:bg-rose-500/30 text-rose-200 hover:text-white font-bold px-3.5 py-2 rounded-full text-xs border border-rose-500/30 disabled:opacity-60"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {confirmDel && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6">
          <div className="bg-[#0a0a0a] border border-rose-500/30 rounded-2xl p-6 max-w-md w-full">
            <div className="text-[11px] uppercase tracking-[0.3em] text-rose-400 font-bold inline-flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" /> Permanent delete
            </div>
            <h3 className="font-display font-black text-2xl text-white mt-2">Delete this stream forever?</h3>
            <p className="text-white/65 text-sm mt-2">
              <strong className="text-white">{confirmDel.title || confirmDel.id}</strong> and its chat history will be permanently removed. This can't be undone.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                data-testid="stream-review-confirm-cancel"
                onClick={() => setConfirmDel(null)}
                className="bg-white/10 hover:bg-white/20 text-white font-bold px-4 py-2 rounded-full text-sm"
              >Cancel</button>
              <button
                data-testid="stream-review-confirm-delete"
                onClick={() => act(confirmDel.id, "delete")}
                className="bg-rose-500 hover:bg-rose-600 text-white font-display font-black px-4 py-2 rounded-full text-sm inline-flex items-center gap-1.5"
              >
                <Trash2 className="w-3.5 h-3.5" /> Delete forever
              </button>
            </div>
          </div>
        </div>
      )}

      {notifyTarget && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6">
          <div className="bg-[#0a0a0a] border border-sky-500/30 rounded-2xl p-6 max-w-md w-full">
            <div className="text-[11px] uppercase tracking-[0.3em] text-sky-300 font-bold inline-flex items-center gap-1.5">
              <Bell className="w-3.5 h-3.5" /> Notify host
            </div>
            <h3 className="font-display font-black text-2xl text-white mt-2">Send a note to {notifyTarget.creator_name || "the host"}</h3>
            <p className="text-white/60 text-sm mt-2">
              Quick in-app notification about <strong className="text-white">{notifyTarget.title || notifyTarget.id}</strong>. Leave the message blank to send the default note.
            </p>
            <textarea
              data-testid="stream-review-notify-msg"
              value={notifyMsg}
              onChange={(e) => setNotifyMsg(e.target.value)}
              maxLength={280}
              rows={3}
              placeholder="Optional message — e.g. 'We've kept this draft for now, please review and decide.'"
              className="mt-3 w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-sky-400 placeholder:text-white/35 resize-none"
            />
            <div className="text-white/35 text-[10px] mt-1">{notifyMsg.length}/280</div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                data-testid="stream-review-notify-cancel"
                onClick={() => { setNotifyTarget(null); setNotifyMsg(""); }}
                className="bg-white/10 hover:bg-white/20 text-white font-bold px-4 py-2 rounded-full text-sm"
              >Cancel</button>
              <button
                data-testid="stream-review-notify-send"
                disabled={busy[notifyTarget.id] === "notify"}
                onClick={() => act(notifyTarget.id, "notify", { message: notifyMsg })}
                className="bg-sky-500 hover:bg-sky-400 text-black font-display font-black px-4 py-2 rounded-full text-sm inline-flex items-center gap-1.5 disabled:opacity-60"
              >
                {busy[notifyTarget.id] === "notify" ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Bell className="w-3.5 h-3.5" />}
                Send
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="text-white/30 text-[10px] inline-flex items-center gap-1.5 mt-4">
        <FileText className="w-3 h-3" /> Audit trail: every delete writes to <code>admin_actions</code>.
      </div>
    </div>
  );
}
